import {
  Input,
  OnInit,
  Output,
  Component,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostListener,
  signal,
  computed,
} from '@angular/core';

import { NgpMoveEvent, NgpMove } from 'ng-primitives/interactions';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { HelpContentService } from '../core/help-content.service';

import { HelpContentState, DimensionValue } from '../core/help.models';

import { Observable } from 'rxjs';
import { NgStyle, AsyncPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';

/**
 * Panel content component - draggable help panel (created by portal service at document.body)
 */
@Component({
  selector: 'app-help-panel-content',
  templateUrl: './help-panel-content.component.html',
  styleUrl: './help-panel.component.scss',
  imports: [NgpMove, NgStyle, FaIconComponent, AsyncPipe, SuiButtonComponent],
})
export class HelpPanelContentComponent implements OnInit {
  @ViewChild('panelElement', { static: false })
  panelElement?: ElementRef<HTMLElement>;

  @Input() helpKey!: string;
  @Input() title?: string;

  @Input() width?: DimensionValue;
  @Input() height?: DimensionValue;
  @Input() minWidth?: DimensionValue;
  @Input() minHeight?: DimensionValue;
  @Input() maxWidth?: DimensionValue;
  @Input() maxHeight?: DimensionValue;

  @Input() initialTop?: string;
  @Input() initialLeft?: string;

  @Output() closed = new EventEmitter<void>();

  contentState$!: Observable<HelpContentState>;

  faExclamationTriangle = faExclamationTriangle;

  titleId = `help-panel-title-${Math.random().toString(36).substr(2, 9)}`;
  displayTitle: string = '';

  /** The panel's absolute position, set by the portal's placement and by dragging; a signal so OnPush repaints it. */
  private readonly position = signal({ left: 100, top: 100 });

  constructor(
    private helpContentService: HelpContentService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    if (!this.helpKey) {
      console.warn('HelpPanelContentComponent: helpKey is required');
      return;
    }

    // * Set title - use provided or translate default
    this.displayTitle =
      this.title || this.translate.instant('help.default-title');

    this.contentState$ = this.helpContentService.getContent(this.helpKey);

    this.moveTo(
      this.initialLeft ? parseFloat(this.initialLeft) : this.position().left,
      this.initialTop ? parseFloat(this.initialTop) : this.position().top,
    );

    // * clamp initial position to Viewport
    setTimeout(() => this.clampPosition(), 0);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.clampPosition();
  }

  close(): void {
    this.closed.emit();
  }

  onMove(event: NgpMoveEvent): void {
    const { left, top } = this.position();
    this.moveTo(left + event.deltaX, top + event.deltaY);
  }

  /** Puts the panel at a position, kept inside the viewport by the panel's current size. */
  moveTo(left: number, top: number): void {
    const panel = this.panelElement?.nativeElement;
    const maxLeft = window.innerWidth - (panel?.offsetWidth ?? 0);
    const maxTop = window.innerHeight - (panel?.offsetHeight ?? 0);
    this.position.set({
      left: Math.max(0, Math.min(left, maxLeft)),
      top: Math.max(0, Math.min(top, maxTop)),
    });
  }

  readonly customStyles = computed((): Record<string, string> => {
    const styles: Record<string, string> = {};
    const { left, top } = this.position();
    styles['left'] = `${left}px`;
    styles['top'] = `${top}px`;

    if (this.width) {
      styles['width'] = this.resolveDimension(this.width);
    }
    if (this.height) {
      styles['height'] = this.resolveDimension(this.height);
    }
    if (this.minWidth) {
      styles['min-width'] = this.resolveDimension(this.minWidth);
    }
    if (this.minHeight) {
      styles['min-height'] = this.resolveDimension(this.minHeight);
    }
    if (this.maxWidth) {
      styles['max-width'] = this.resolveDimension(this.maxWidth);
    }
    if (this.maxHeight) {
      styles['max-height'] = this.resolveDimension(this.maxHeight);
    }

    return styles;
  });

  private resolveDimension(value: DimensionValue): string {
    return typeof value === 'number' ? `${value}px` : value;
  }

  // * clamp Panel position to Viewport boundaries
  private clampPosition(): void {
    const { left, top } = this.position();
    this.moveTo(left, top);
  }
}
